import { useCallback, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { v4 as uuidv4 } from 'uuid';
import { useHighlightedItem } from '@/hooks';
import { formSchema, type FormValues } from './validationSchema';

import type { DragEndEvent } from '@dnd-kit/core';
import type { FieldErrors, UseFormReturn, FieldArrayWithId } from 'react-hook-form';
import type { GlobalPromptQuestion, SaveGlobalQuestion } from '../types';

interface UseGlobalPromptDraftsResult {
    fields: FieldArrayWithId<FormValues, 'questions', 'fieldKey'>[];
    fieldKeys: string[];
    highlightedItemId: string | null;
    errors: FieldErrors<FormValues>;
    sensors: ReturnType<typeof useSensors>;
    isSortingDisabled: boolean;
    count: number;
    formMethods: UseFormReturn<FormValues>;
    handleAddQuestion: () => void;
    handleDelete: (index: number) => void;
    handleReorder: (from: number, to: number) => void;
    handleDragEnd: (event: DragEndEvent) => void;
    handleSave: () => void;
}

const toFormQuestion = (q: GlobalPromptQuestion): FormValues['questions'][number] => ({
    questionId: q.questionId,
    questionTitle: q.questionTitle ?? '',
    questionText: q.questionText,
    questionType: q.questionType,
});

const emptyQuestion = (): FormValues['questions'][number] => ({
    questionId: uuidv4(),
    questionTitle: '',
    questionText: '',
    questionType: 'text',
});

const useGlobalPromptDrafts = (
    initialQuestions: GlobalPromptQuestion[],
    onSave: (questions: SaveGlobalQuestion[]) => void | Promise<void>,
): UseGlobalPromptDraftsResult => {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    const formMethods = useForm<FormValues>({
        defaultValues: {
            questions: initialQuestions.length > 0 ? initialQuestions.map(toFormQuestion) : [emptyQuestion()],
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: zodResolver(formSchema),
    });

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = formMethods;

    const { fields, append, remove, move } = useFieldArray({
        control,
        name: 'questions',
        keyName: 'fieldKey', // Stable per-row key generated by react-hook-form, also used as the sortable id
    });

    const fieldKeys = useMemo(() => fields.map(f => f.fieldKey), [fields]);
    const { highlightedItemId, highlightItem } = useHighlightedItem(fieldKeys);

    const handleAddQuestion = useCallback(() => append(emptyQuestion()), [append]);

    const handleDelete = useCallback((index: number) => remove(index), [remove]);

    const handleReorder = useCallback(
        (from: number, to: number) => {
            if (from === to) return;

            move(from, to);
            highlightItem(fields[from].fieldKey);
        },
        [move, fields, highlightItem],
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
                const oldIndex = fields.findIndex(f => f.fieldKey === active.id);
                const newIndex = fields.findIndex(f => f.fieldKey === over.id);
                if (oldIndex !== -1 && newIndex !== -1) {
                    move(oldIndex, newIndex);
                    highlightItem(active.id as string);
                }
            }
        },
        [fields, move, highlightItem],
    );

    const onSubmit = useCallback(
        (data: FormValues) => {
            onSave(
                data.questions.map(q => ({
                    questionId: q.questionId,
                    questionTitle: q.questionTitle.trim() || null,
                    questionText: q.questionText.trim(),
                    questionType: q.questionType,
                })),
            );
        },
        [onSave],
    );

    return {
        fields,
        fieldKeys,
        highlightedItemId,
        errors,
        sensors,
        isSortingDisabled: fields.length < 2,
        count: fields.length,
        formMethods,
        handleAddQuestion,
        handleDelete,
        handleReorder,
        handleDragEnd,
        handleSave: handleSubmit(onSubmit),
    };
};

export default useGlobalPromptDrafts;
